<template>
  <div class="p-learning-detail-cont__wrapper">
    <div class="p-learning-detail-cont">
      <div class="learningDetailCont__main">
        <div class="p-learning-detail-figure p-learning-detail-figure--block">
          <p class="ChartUnit ChartUnit--scatter num">（%）</p>
          <div class="figureScatter">
            <div class="figureScatter__ylabel">{{figureScatterAnswerRateLabel}}</div>
            <div class="figureScatter__chart">
              <canvas :id="chartMeta.chartId"></canvas>
            </div>
            <div class="figureScatter__unit">（%）</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {ref, computed, onMounted, onUnmounted, watch} from "vue";
  import Chart from 'chart.js/auto' // NFR-6251 4.x では “auto” 推奨（core+plugin自動登録）
  import type { ChartOptions } from 'chart.js'
  import { CORRECT_ANSWER_RATE_TYPE_LABEL_MAP } from "@/consts/correct_answer_rate";
  import { PERIODS_TYPE_LABEL_MAP } from "@/consts/display_period";

  // FIXME: 特定の親子間でしか使わないので、ひとまずここに置いておきます
  export interface ScatterChartMeta {
    chartId: string;
  }
  const props = defineProps<{
    chartMeta: ScatterChartMeta;
    chartData: any;
    correctAnswerRateType: number;
    periodType: number;
  }>()

  // NFR-7201 reactive 化すると destroy() が proxy 経由となり animator から raw インスタンスを除去できないため、プレーンな変数で保持する
  let chart: Chart | null = null;
  const xVal = ref(null)
  const yVal = ref(null)
  const o1Val = ref(null)
  const o2Val = ref(null)
  const answerRateLabel = ref(null)
  const studyTimeLabel = ref(null)

  const periodTypeLabel = computed(() => {
      return PERIODS_TYPE_LABEL_MAP[props.periodType]
  })

  const figureScatterAnswerRateLabel = computed(() => {
      return CORRECT_ANSWER_RATE_TYPE_LABEL_MAP[props.correctAnswerRateType] + periodTypeLabel.value
  })

  const scatterChartData = computed(() => {
    let datasets = [];
    const averageData = props.chartData.averageData;

    // 表でも使っている同じ参照元のデータをいじるので、影響でないようdeepcopyを作成する
    const chartDatasets = props.chartData.datasets.map(el => el);
    // 平均値を表示用データのリストに追加
    chartDatasets.push(averageData)

    chartDatasets.map((dataset, index) => {
      // 初回正答率
      var datasetYValue = dataset.correctAnswerRateInitial
      if (props.correctAnswerRateType == 2){
        // 最新正答率
        datasetYValue = dataset.correctAnswerRateLatest
      }
      const chartDataset = {
        label: dataset.name,
        data: [
          {
            x: dataset.progressRate,
            y: datasetYValue,
            other1: dataset.studiedHoursLatest,
            other2: dataset.studiedHoursDifference,
            answerRateLabel: figureScatterAnswerRateLabel.value,
            studyTimeLabel: "学習時間" + periodTypeLabel.value
          },
        ],
        borderColor: "rgba(204, 204, 204, 1)",
        backgroundColor: "rgba(204, 204, 204, 1)",
        pointRadius: 5,
        clip: false,
      };
      if (chartDataset.data[0].x > averageData.progressRate) {
        // 平均より上
        chartDataset.borderColor = "rgba(41, 101, 247, 1)";
        chartDataset.backgroundColor = "rgba(41, 101, 247, 1)";
      } else if (chartDataset.data[0].x < averageData.progressRate) {
        // 平均より下
        chartDataset.borderColor = "rgba(226, 86, 18, 1)";
        chartDataset.backgroundColor = "rgba(226, 86, 18, 1)";
      }
      datasets.push(chartDataset);
    });

    return {
      datasets: datasets,
    };
  })

  const scatterChartOptions = ref<ChartOptions<'scatter'>>({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        enabled: true,
        mode: 'nearest',
        intersect: true,
        backgroundColor: 'rgba(221,221,221,1)',
        borderColor: 'rgba(0,0,0,1)',
        borderWidth: 0,
        padding: 10,
        titleFont: { size: 17, family: 'fot-udmarugo-large-pr6n', weight: 700 },
        titleColor: 'rgba(51,51,51,1)',
        bodyFont: { size: 14, family: 'fot-udmarugo-large-pr6n', weight: 700 },
        bodyColor:  'rgba(51,51,51,1)', 
        footerFont: { size: 14, family: 'fot-udmarugo-large-pr6n', weight: 700 },
        footerColor:'rgba(51,51,51,1)', 
        footerMarginTop: 15,
        footerSpacing: 10,
        callbacks: {
          label(ctx) {
            return ctx.dataset.label as string
          },
          afterLabel(ctx) {
            const p: any = ctx.raw
            xVal.value = p.x
            yVal.value = p.y
            o1Val.value = p.other1
            o2Val.value = p.other2
            answerRateLabel.value = p.answerRateLabel
            studyTimeLabel.value = p.studyTimeLabel
            return ''
          },
          afterFooter() {
            const studyTime = `${studyTimeLabel.value}：${Math.floor(o1Val.value / 60)}時間${
              o1Val.value % 60
            }分`
            const diff = Math.sign(o2Val.value) === -1 ? '-' : ''
            const diffStr =
              diff +
              `${Math.floor(Math.abs(o2Val.value) / 60)}時間${Math.abs(o2Val.value) % 60}分`

            return [
              `　 ${answerRateLabel.value}：${yVal.value}％`,
              `　 進捗率：${xVal.value}％`,
              `　 ${studyTime}`,
              `　 前期間比：${diffStr}`,
            ]
          },
        },
      },
    },
    scales: {
      x: {
        min: 0,
        max: 100,
        title: {
          display: true,
          text: '進捗率',
          color: 'rgba(51, 51, 51, 1)',
          font: { size: 17, family: 'fot-udmarugo-large-pr6n', weight: 700 },
        },
        grid: { display: true },
        ticks: {
          padding: 10,
          stepSize: 25,
          autoSkip: false,
          color: 'rgba(51, 51, 51, 1)',
          font: { size: 16, family: 'oswald, sans-serif' },
        },
      },
      y: {
        min: 0,
        max: 100,
        title: {
          display: false,
          color: 'rgba(51, 51, 51, 1)',
          font: { size: 17, family: 'fot-udmarugo-large-pr6n', weight: 700 },
        },
        grid: {
          color: ctx =>
            ctx.tick.value === 0 ? 'rgba(51, 51, 51, 1)' : 'rgba(221, 221, 221, 1)',
          lineWidth: ctx => (ctx.tick.value === 0 ? 2 : 1),
        },
        border: {
          display: false,
          color: "rgba(51, 51, 51, 1)",
        },
        ticks: {
          padding: 10,
          stepSize: 25,
          autoSkip: false,
          color: 'rgba(51, 51, 51, 1)',
          font: { size: 16, family: 'oswald, sans-serif' },
        },
      },
    },
  })

  onMounted(() => {
    updateChartYAxesLabelString()
    const ctx = document.getElementById(props.chartMeta.chartId);
    drawScatterChart(ctx);
  })

  function drawScatterChart(ctx) {
    chart = new Chart(ctx, {
      type: "scatter",
      data: scatterChartData.value as any,
      options: scatterChartOptions.value as any,
    });
  }

  watch(
    () => [props.chartData, props.correctAnswerRateType, props.periodType],
    () => {
      chart?.destroy()
      chart = null
      const ctx = document.getElementById(props.chartMeta.chartId) as HTMLCanvasElement
      if (ctx) drawScatterChart(ctx);
    },
    { deep: true }
  )

  onUnmounted(() => {
    chart?.destroy()
    chart = null
  })
  
  watch(
    () => figureScatterAnswerRateLabel.value,
    () => {
      updateChartYAxesLabelString()
    }
  )

  function updateChartYAxesLabelString() {
    ;(scatterChartOptions.value.scales!.y as any).title.text = figureScatterAnswerRateLabel.value
  }

</script>

